/**
 * ATT-2872 admin half — tenant discovery store (Zustand).
 *
 * Boot-time identity hydration. ATT-3383: tenant resolution is registry-only,
 * so discovery runs for every host. Initial state matches the first-paint
 * bootstrap returned by `config/tenants.ts#getTenantConfig` — empty discoverable
 * fields, source "bootstrap" — so synchronous reads never see undefined.
 *
 * `useTenantDiscovery()` updates `source` based on outcome:
 *   - "bootstrap"   → initial, env-derived; backend not yet contacted.
 *   - "discovered"  → backend response parsed and applied successfully.
 *   - "failed"      → fetch error OR top-level Zod failure (Sentry warning
 *                     already captured by the shared core fetcher);
 *                     bootstrap values remain.
 *
 * Disjoint from `useTenantSettingsStore` (ATT-2871) by design (see §D3).
 */

import { create } from "zustand";

export type TenantDiscoverySource = "bootstrap" | "discovered" | "failed";

export type TenantDiscoveryState = {
  source: TenantDiscoverySource;
  /**
   * ATT-3785: discovery is still in flight but has already outrun
   * `DISCOVERY_SLOW_AFTER_MS` — almost always a backend cold start. Orthogonal
   * to `source` on purpose: the run has NOT failed and must keep waiting, so
   * `source` stays "bootstrap"; this flag only lets the gate explain the wait
   * instead of showing a spinner that looks hung. Always false in any settled
   * state.
   */
  slow: boolean;
  displayName: string;
  hideLanguages: string[];
  featureFlags: string[];
  logoUrl: string | null;
  setBootstrap: (s: {
    displayName?: string;
    hideLanguages?: string[];
    featureFlags?: string[];
    logoUrl?: string | null;
  }) => void;
  setSlow: () => void;
  setDiscovered: (s: Partial<
    Pick<TenantDiscoveryState, "displayName" | "hideLanguages" | "featureFlags" | "logoUrl">
  >) => void;
  setFailed: () => void;
};

export const useTenantDiscoveryStore = create<TenantDiscoveryState>((set) => ({
  source: "bootstrap",
  slow: false,
  displayName: "",
  hideLanguages: [],
  featureFlags: [],
  logoUrl: null,
  setBootstrap: (s) =>
    set((prev) => ({
      ...prev,
      source: "bootstrap",
      slow: false,
      displayName: s.displayName ?? "",
      hideLanguages: s.hideLanguages ?? [],
      featureFlags: s.featureFlags ?? [],
      logoUrl: s.logoUrl ?? null,
    })),
  setSlow: () => set((prev) => ({ ...prev, slow: true })),
  setDiscovered: (s) =>
    set((prev) => ({ ...prev, ...s, source: "discovered", slow: false })),
  setFailed: () => set((prev) => ({ ...prev, source: "failed", slow: false })),
}));
